type Feature = {
  name: string;
  access: string;
  description: string;
  setup: string;
};

export const featureGroups: { title: string; items: Feature[] }[] = [
  {
    title: "Voicemail",
    items: [
      {
        name: "Check voicemail",
        access: "*97",
        description:
          "Open the mailbox assigned to the calling phone. A mailbox PIN is required.",
        setup: "Configure the mailbox and its phone access before activation.",
      },
      {
        name: "Voicemail button",
        access: "Messages / Voicemail button",
        description: "Open the same mailbox without entering the access code.",
        setup: "Program the phone's voicemail button to dial *97.",
      },
      {
        name: "Message-waiting indicator (MWI)",
        access: "Phone light or message icon",
        description:
          "Show when the assigned mailbox has new messages; clear after they are handled.",
        setup:
          "Requires mailbox-to-phone notification setup and a compatible phone.",
      },
      {
        name: "Voicemail playback",
        access: "Customer → Voicemail / Features",
        description:
          "Review line voicemail in Voicemail and phone-menu mailbox messages in Features.",
        setup: "Available after the corresponding mailbox is installed.",
      },
    ],
  },
  {
    title: "Call handling",
    items: [
      {
        name: "Do Not Disturb",
        access: "Customer → Features",
        description: "Stop incoming calls from ringing the selected line.",
        setup:
          "Save the setting, then apply call handling to the installed line.",
      },
      {
        name: "Call forwarding",
        access: "Customer → Features",
        description:
          "Forward all calls, busy calls, or unanswered calls to the configured number.",
        setup: "Requires an installed line and an approved outgoing route.",
      },
      {
        name: "Call waiting",
        access: "Customer → Features",
        description: "Allow another incoming call while the line is in use.",
        setup:
          "Apply call handling; the phone must also support a second call.",
      },
      {
        name: "Ring time and unanswered calls",
        access: "Customer → Features",
        description:
          "Set how long a line or menu destination rings and where unanswered calls go.",
        setup:
          "Line handling and phone-menu ring times are configured separately.",
      },
      {
        name: "Caller ID",
        access: "Customer → Features / Numbers",
        description:
          "Manage caller-name preferences and the number assigned for outgoing calls.",
        setup:
          "Carrier and phone activation are required; a saved preference is not proof of live Caller ID.",
      },
      {
        name: "3-way calling",
        access: "Phone conference controls",
        description: "Join two other people on a call.",
        setup:
          "Phone dependent. The portal records the request; handset setup must be verified.",
      },
      {
        name: "Call return",
        access: "*69 · commissioned company phones",
        description: "Call back the last eligible incoming caller.",
        setup:
          "Not a universal customer code. Confirm line activation before providing it to a customer.",
      },
    ],
  },
  {
    title: "Phone menus and service",
    items: [
      {
        name: "Phone menus (IVR)",
        access: "Customer → Features",
        description:
          "Add, edit, or delete a customer's greeting and keypad menu. Route choices to a phone, announcement, or voicemail.",
        setup:
          "Requires a commissioned incoming number. Key 9 repeats the menu; other keys are configurable.",
      },
      {
        name: "Recorded announcements",
        access: "Customer → Features / Voice studio",
        description:
          "Provide service times, an address, or other information with natural-sounding recordings.",
        setup: "Menu announcements are saved with the customer's phone menu.",
      },
      {
        name: "Extensions and equipment",
        access: "Customer → Users / Equipment",
        description:
          "Assign internal extensions and connect the correct physical phone to a line.",
        setup:
          "Equipment records, phone configuration, registration, and call testing are separate steps.",
      },
      {
        name: "Emergency calling",
        access: "Customer → Numbers / Service setup",
        description:
          "Associate the calling number with the verified service location.",
        setup:
          "Verify carrier emergency-service provisioning before activating outbound service.",
      },
    ],
  },
];

export function FeatureList({ onCustomers }: { onCustomers: () => void }) {
  return (
    <div className="cr-feature-list">
      <section className="cr-panel">
        <p>
          Calling features, access codes, and setup locations for staff.
          Availability depends on the customer's installed line and phone.
        </p>
        <button className="cr-button" onClick={onCustomers}>
          Manage customer features
        </button>
        <p className="cr-form-note">
          Open a customer, then choose Features. Mailbox PINs are private and
          are not listed here.
        </p>
      </section>
      {featureGroups.map((group) => (
        <section className="cr-panel" key={group.title}>
          <h2>{group.title}</h2>
          <dl className="cr-feature-reference">
            {group.items.map((feature) => (
              <div key={feature.name}>
                <dt>{feature.name}</dt>
                <dd>
                  <strong>{feature.access}</strong>
                  <p>{feature.description}</p>
                  <p className="cr-form-note">{feature.setup}</p>
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}
